import {Suspense} from "react";
import classNames from 'classnames';
import Link from "next/link";
import {AnimatePresence} from "framer-motion";
import dynamic from "next/dynamic";
import Image from "next/image";

import {useStickyNewsletterStore} from "@/lib/stores/layout";
import ImgLogo from "@/lib/assets/images/logo-footer-w.svg?url";
import {getReactiveContent, getStyledTitle} from "@/lib/utils/helpers";
import Container from '@/components/Container/Container';
import Heading from '@/components/Heading/Heading';
import NavigationMenu from '@/components/NavigationMenu/NavigationMenu';
import Loading from "@/components/Loading/Loading";
import SocialProfiles from "@/components/SocialProfiles/SocialProfiles";

const NewsletterPopup = dynamic(() => import('@/components/Newsletter/NewsletterPopup').then((mod) => mod.default));
const Newsletter = dynamic(() => import('@/components/Newsletter/Newsletter').then((mod) => mod.default));

export default function Footer(
  {
    subMenuA,
    subMenuB,
    siteSettings,
    className = 'mt-8 lg:mt-10'
  }
) {
  const year = new Date().getFullYear();
  const isPopupVisible = useStickyNewsletterStore(state => state.visible);

  return (
    <Suspense fallback={<Loading/>}>
      <footer
        className={classNames(
          className,
          'relative after:content-[\'\'] after:absolute after:top-[calc(100%-1px)] after:left-0 after:w-full',
          'after:h-[50px] md:after:h-[90px]'
        )}
      >
        <Container className={`mom-section`}>
          <SocialProfiles {...siteSettings}/>
        </Container>

        <Container className={`mom-section`}>
          <div className={classNames(
            `md:flex md:flex-wrap md:items-start md:justify-between md:flex-row`,
            '[&_li]:block [&_li]:py-2',
            'max-md:grid max-md:grid-cols-2 max-md:gap-x-4'
          )}>
            <NavigationMenu
              menuItems={subMenuA}
              className={`flex items-start justify-center md:justify-start md:w-1/3 order-2 md:order-none`}
            />

            <div className={classNames(
              'text-center',
              'md:w-1/3 order-1 md:order-none',
              'max-md:px-6 max-md:mb-12 max-md:col-span-2'
            )}>
              <div className="mom-hidden-auth">
                <Heading level={'h3'} className={`lg:text-[34px]`}>
                  {getStyledTitle({title: 'Want to join  our exclusive community?', italicLast: false})}
                </Heading>
                <Newsletter showAwards={true} heading={''} description={''}/>
              </div>

              <Link
                href={'/'}
                className={classNames(
                  'mt-14',
                  'inline-block',
                  'w-24 max-w-24 lg:w-32 lg:max-w-32',
                  '[body.mom-user_&]:!mt-0'
                )}
                aria-label={'Man of Many'}
                prefetch={false}
              >
                <Image src={ImgLogo} alt={'Man of Many'} className={`w-full mom-invert--on-light`}/>
              </Link>
            </div>

            <NavigationMenu
              menuItems={subMenuB}
              className={`flex items-start justify-center md:w-1/3 order-3 md:order-none md:justify-end md:text-right`}
            />
          </div>
        </Container>

        <Container className={`mom-section text-center text-sm`}>
          <div className="mx-auto max-w-[885px]">
            {siteSettings?.footerAffiliateDisclaimer && (
              <>
                {getReactiveContent({htmlString: siteSettings.footerAffiliateDisclaimer, applyAutoP: true})}
              </>
            )}

            <p className={`text-gray-500 dark:text-gray`} suppressHydrationWarning>
              © {year} Man of Many Pty Ltd – Sydney, Australia ABN 73&nbsp;163&nbsp;331&nbsp;280<br/>
              MAN OF MANY® and its Logos are registered trademarks of Man of Many Pty Ltd.
            </p>
          </div>
        </Container>
      </footer>

      <AnimatePresence>
        {isPopupVisible && (
          <NewsletterPopup/>
        )}
      </AnimatePresence>
    </Suspense>
  );
}
